import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { UiProperties } from '../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { ZiroFileUploadConfig } from '../../../forms/fields/file-upload/ziro-file-upload.component';
import { ZiroCodeAreaConfig, ZiroCodeAreaMode } from '../../../forms/fields/code-area/ziro-code-area.component';
import { Observable, Subscription, throwError } from 'rxjs';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { CustomizeUiPreviewModalComponent } from './customize-ui-preview-modal.component';
import { CustomizeUiFullPreviewModalComponent } from './customize-ui-full-preview-modal.component';
import { CustomizeUiResource } from '../../../shared/resources/customize-ui.resource';
import { CustomUiOptionsContext } from '../../../shared/contexts/custom-ui-options.context';
import { HtmlSanitizerService } from '../../../shared/services/html-sanitizer.service';
import { GlobalPropertiesContext } from '../../../shared/contexts/global-properties.context';
import { DomSanitizer, SafeHtml, SafeResourceUrl, SafeStyle } from '@angular/platform-browser';

export enum CustomizeUiPhotoTypes {
  LOGO = 'LOGO',
  BACKGROUND_IMAGE = 'BACKGROUND_IMAGE',
}

@Component({
  selector: 'ziro-admin-customize-ui',
  templateUrl: './customize-ui.component.html',
  styleUrls: ['./customize-ui.component.scss', '../../admin-page.scss'],
  standalone: false,
})
export class CustomizeUiComponent extends ZiroFormAbstractDirective<UiProperties> implements OnInit, OnDestroy {
  private toastService = inject(ToastService);
  private bottomNavService = inject(BottomNavService);
  private ziroModalService = inject(ZiroModalService);
  private translateService = inject(TranslateService);
  protected _ziroFormStateService: ZiroFormStateService;
  private breadcrumbsService = inject(BreadcrumbsService);
  private customizeUiResource = inject(CustomizeUiResource);
  private _uiOptionsContext = inject(CustomUiOptionsContext);
  private _htmlSanitizerService = inject(HtmlSanitizerService);
  private globalPropertiesContext = inject(GlobalPropertiesContext);
  private domSanitizer = inject(DomSanitizer);

  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  customizeUiPhotoTypes = CustomizeUiPhotoTypes;
  isLoading = true;
  uiProperties: UiProperties;
  isHosted = false;
  previewLogo: string | SafeResourceUrl = '/static/img/ziro-login-logo-sm.png';
  previewBackgroundStyles: SafeStyle;
  supportTextHtmlPreview: SafeHtml;
  loginButtonLabel: string;
  formConfig: ZiroFormConfig = {
    fields: {
      supportTextHtml: {
        dataAutomation: 'customize-ui-support-text',
        label: 'tkey;admin.ui.support.html_content.label',
        helpText: 'tkey;admin.ui.support.html_content.helptext',
        componentConfig: new ZiroCodeAreaConfig({
          mode: ZiroCodeAreaMode.HTML,
          placeholder: 'tkey;admin.ui.support.html_content.placeholder',
        }),
        validation: [
          {
            validator: (val: string) => {
              this._supportTextUnsafeHtml = this._htmlSanitizerService.checkForUnsafeHtml(val);
              return !this._supportTextUnsafeHtml ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
            },
            message: () => {
              return {
                content: 'tkey;validators.global.unsafe_html',
                params: { html: this._supportTextUnsafeHtml },
              };
            },
          },
        ],
      },
      logo: {
        dataAutomation: 'customize-ui-logo',
        label: 'tkey;admin.ui.form.logo.label',
        helpText: 'tkey;admin.ui.form.logo.helptext',
        componentConfig: new ZiroFileUploadConfig({
          acceptedFileExtensions: { acceptedExtensions: '.jpg,.jpeg,.png' },
          maxKbSize: 20000,
        }),
      },
      backgroundImage: {
        dataAutomation: 'customize-ui-background-image',
        label: 'tkey;admin.ui.form.background_image.label',
        helpText: 'tkey;admin.ui.form.background_image.helptext',
        componentConfig: new ZiroFileUploadConfig({
          acceptedFileExtensions: { acceptedExtensions: '.jpg,.jpeg,.png' },
          maxKbSize: 20000,
        }),
      },
      backgroundColor: {
        dataAutomation: 'customize-ui-background-color',
        label: 'tkey;admin.ui.form.background_color.label',
        helpText: 'tkey;admin.ui.form.background_color.helptext',
        required: true,
      },
    },
  };

  private defaultBackgroundColor = '#202020';
  private _supportTextUnsafeHtml: string;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.ui.title' }]);
    this._initBottomNav();
    this.loginButtonLabel = this.translateService.instant('tkey;pages.login.submit');

    const globalPropertiesSub = this.globalPropertiesContext.state$.subscribe((globalProperties) => {
      this.isHosted = globalProperties.hostedEnabled;
    });
    this._subscriptions.add(globalPropertiesSub);

    this.customizeUiResource.getUiProperties().subscribe((uiProperties: UiProperties) => {
      this.uiProperties = uiProperties;
      const supportText = uiProperties.supportTextHtml || '';
      this.entitySource.next({
        supportTextHtml: supportText,
        backgroundColor: uiProperties.backgroundColor || this.defaultBackgroundColor,
      } as UiProperties);
      this._updatePreview();
      this._updateSupportTextPreview(supportText);
      this.isLoading = false;
    });

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((update) => {
      if (!this.isLoading) {
        this._updatePreview();
        const newSupportText = update.new.supportTextHtml;
        if (newSupportText != null) {
          this._updateSupportTextPreview(newSupportText);
        }
      }
    });
    this._subscriptions.add(formUpdateSub);
  }

  private _updateSupportTextPreview(htmlText: string): void {
    if (htmlText) {
      this.supportTextHtmlPreview = this._htmlSanitizerService.sanitizeAndTrustHtml(htmlText);
    } else {
      this.supportTextHtmlPreview = null;
    }
  }

  private _updatePreview(): void {
    const logoValue = this.formData.logo as string | File | undefined;
    if (logoValue && logoValue instanceof File) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewLogo = this.domSanitizer.bypassSecurityTrustResourceUrl(e.target.result);
      };
      reader.readAsDataURL(logoValue);
    } else if (this.uiProperties?.logo) {
      this.previewLogo = this.domSanitizer.bypassSecurityTrustResourceUrl(
        `data:image/png;base64,${this.uiProperties.logo}`,
      );
    } else {
      this.previewLogo = '/static/img/ziro-login-logo-sm.png';
    }

    const backgroundColor = this.formData.backgroundColor || this.defaultBackgroundColor;
    let styles = `background-color: ${backgroundColor};`;

    const backgroundImageValue = this.formData.backgroundImage as string | File | undefined;
    if (backgroundImageValue && backgroundImageValue instanceof File) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        styles += `background-image: url("${e.target.result}");`;
        styles += `background-size: 100% auto; background-repeat: no-repeat; background-position: top center;`;
        this.previewBackgroundStyles = this.domSanitizer.bypassSecurityTrustStyle(styles);
      };
      reader.readAsDataURL(backgroundImageValue);
    } else if (this.uiProperties?.backgroundImage) {
      styles += `background-image: url("data:image/png;base64,${this.uiProperties.backgroundImage}");`;
      styles += `background-size: 100% auto; background-repeat: no-repeat; background-position: top center;`;
      this.previewBackgroundStyles = this.domSanitizer.bypassSecurityTrustStyle(styles);
    } else {
      this.previewBackgroundStyles = this.domSanitizer.bypassSecurityTrustStyle(styles);
    }
  }

  ngOnDestroy(): void {
    this.breadcrumbsService.clearBreadcrumbs();
    this._subscriptions.unsubscribe();
  }

  onDeleteClick(type: CustomizeUiPhotoTypes): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        promptBody: this.translateService.instant('tkey;admin.ui.modal.delete.message'),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => (type === 'LOGO' ? this._deleteLogo() : this._deleteBackgroundImage()),
          },
        ],
      },
    };

    this.ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  onPreviewClick(type: CustomizeUiPhotoTypes): void {
    this.ziroModalService.openCustomModal(
      CustomizeUiPreviewModalComponent,
      {
        type: type,
        image: type === CustomizeUiPhotoTypes.LOGO ? this.uiProperties.logo : this.uiProperties.backgroundImage,
      },
      { size: type === CustomizeUiPhotoTypes.LOGO ? 'lg' : 'xxl' },
    );
  }

  onFullPreviewClick(): void {
    const showSupportLink = !!this.uiProperties?.supportTextHtml?.trim();
    this.ziroModalService.openCustomModal(
      CustomizeUiFullPreviewModalComponent,
      {
        previewLogo: this.previewLogo,
        previewBackgroundStyles: this.previewBackgroundStyles,
        loginButtonLabel: this.loginButtonLabel,
        showSupportLink: showSupportLink,
      },
      { size: 'xxl' },
    );
  }

  private _deleteLogo(): Observable<void> {
    return new Observable((subscriber) => {
      this.customizeUiResource.deleteImage(CustomizeUiPhotoTypes.LOGO).subscribe(() => {
        this._getUiProperties().subscribe(() => this._showSuccessToast());
        subscriber.next();
        subscriber.complete();
      });
    });
  }

  private _deleteBackgroundImage(): Observable<void> {
    return new Observable((subscriber) => {
      this.customizeUiResource.deleteImage(CustomizeUiPhotoTypes.BACKGROUND_IMAGE).subscribe(() => {
        this._getUiProperties().subscribe(() => this._showSuccessToast());
        subscriber.next();
        subscriber.complete();
      });
    });
  }

  protected submit(): Observable<void> {
    return this._onFormSubmission();
  }

  private _onFormSubmission(): Observable<void> {
    if (!this.isFormValid()) {
      this.bottomNavService.dispatch(new BottomNavUpdateState({ hasValidationError: true }));
    } else {
      this.bottomNavService.dispatch(new BottomNavUpdateState({ hasValidationError: false }));
      this._setPending(true);

      const formData = new FormData();
      if (this.formData.logo) {
        formData.append('logo', this.formData.logo || null);
      }
      if (this.formData.backgroundImage) {
        formData.append('backgroundImage', this.formData.backgroundImage || null);
      }
      formData.append('backgroundColor', this.formData.backgroundColor);
      formData.append('supportTextHtml', this.formData.supportTextHtml);

      return this.customizeUiResource.saveUiProperties(formData).pipe(
        switchMap(() => this._getUiProperties()),
        tap(() => {
          this._showSuccessToast();
          this._setPending(false);
        }),
        catchError((error) => {
          this._setPending(false);
          return throwError(() => error);
        }),
      );
    }
  }

  private _getUiProperties(): Observable<void> {
    return this.customizeUiResource.getUiProperties().pipe(
      switchMap((response: UiProperties) => {
        this.uiProperties = response;
        this.entitySource.next({
          supportTextHtml: response.supportTextHtml,
          backgroundColor: response.backgroundColor || this.defaultBackgroundColor,
        } as UiProperties);
        this._updatePreview();
        return this._uiOptionsContext.refresh();
      }),
    );
  }

  private _initBottomNav(): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'customize-ui-save',
          label: 'tkey;admin.ui.save',
          buttonClass: this.buttonStyles.PRIMARY,
          dataAutomation: 'customize-ui-save-button',
          state: {
            pending: false,
            buttonDisableState: { disabled: false, tooltipKey: '' },
          },
          icon: this.ziroIcons.SAVE_SUCCESS,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );
  }

  private _setPending(setting: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'customize-ui-save',
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _showSuccessToast(): void {
    this.toastService.push(
      ToastTypes.SUCCESS,
      this.ziroIcons.PREVIEW,
      'tkey;shared.toast.save.success.title',
      'tkey;admin.ui.toast.success.message',
    );
  }
}
